import { getAuth } from "firebase/auth";
import React, { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { useTranslation } from "react-i18next";
import { useStreams } from "../hooks/useStreams";
import { Loader2, AlertCircle, Users, Eye, Repeat, Clock } from "lucide-react";
import { app } from "../firebase";
import Sidebar from "./Sidebar";
import Header from "../components/Header";
import {
  AUDIENCE_DAY_OPTIONS,
  AudienceCount,
  AudienceSummary,
  audienceUrl,
  bucketLabel,
  peakBucket,
  percent,
  topWithOther,
  viewerOffsetMinutes,
} from "../utils/audience";

const API_BASE_URL = "https://api.deafassistant.com";

const Stat: React.FC<{ icon: React.ElementType; label: string; value: string; hint?: string }> = ({ icon: Icon, label, value, hint }) => (
  <div className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100">
    <div className="flex items-center gap-2 text-slate-500 text-sm font-bold">
      <Icon size={16} className="text-blue-600" aria-hidden="true" />
      {label}
    </div>
    <div className="text-3xl font-bold text-slate-900 mt-2">{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
  </div>
);

const BarList: React.FC<{ title: string; items: AudienceCount[]; empty: string }> = ({ title, items, empty }) => {
  const max = Math.max(1, ...items.map((i) => i.hits));
  return (
    <section className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100">
      <h2 className="text-lg font-bold text-slate-900 mb-3">{title}</h2>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500">{empty}</p>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <li key={item.key} className="text-sm">
              <div className="flex justify-between gap-3">
                <span className="text-slate-700 truncate">{item.key}</span>
                <span className="font-bold text-slate-900 tabular-nums">{item.hits.toLocaleString()}</span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 mt-1">
                <div className="h-1.5 rounded-full bg-blue-600" style={{ width: `${(item.hits / max) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

const Audience: React.FC = () => {
  const { t } = useTranslation();
  const auth = getAuth(app);
  const [user, loading] = useAuthState(auth);
  const [streamName, setStreamName] = useState("");
  const [days, setDays] = useState(7);
  const [summary, setSummary] = useState<AudienceSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { selected, loading: streamsLoading } = useStreams();
  useEffect(() => {
    if (!user || streamsLoading) return;
    if (selected) setStreamName(selected.name);
    else setIsLoading(false);
  }, [user, selected, streamsLoading]);

  useEffect(() => {
    if (!streamName || !user) return;
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    fetch(audienceUrl(API_BASE_URL, streamName, user.uid, days, viewerOffsetMinutes()), { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`API Error: ${response.status}`);
        return response.json() as Promise<AudienceSummary>;
      })
      .then((data) => {
        setSummary(data);
        setIsLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.error("Failed to fetch audience:", err);
        setError(t("audience.error", "Could not load your audience."));
        setIsLoading(false);
      });
    return () => controller.abort();
  }, [streamName, user, days, t]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  const unknown = t("audience.unknown", "Unknown");
  const other = t("audience.other", "Other");
  const peak = summary ? peakBucket(summary.trend) : null;
  const trendMax = summary ? Math.max(1, ...summary.trend.map((p) => p.hits)) : 1;
  const known = summary ? summary.newVisitorHits + summary.returningVisitorHits : 0;
  const bots = summary?.topDeviceTypes.find((d) => d.key === "bot")?.hits ?? 0;
  const empty = t("audience.none", "No visits yet.");

  return (
    <div className="flex flex-col h-screen bg-slate-50 font-inter overflow-hidden">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar activeItem="audience" />
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-5xl mx-auto space-y-6">
            <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <h1 className="text-4xl font-bold text-slate-900 tracking-tight">{t("audience.title", "Audience")}</h1>
                <p className="text-slate-500 mt-1 text-lg">
                  {t("audience.subtitle", "People who opened your caption page")}{" "}
                  <span className="font-bold text-slate-700">deafassistant.com/{streamName}</span>
                </p>
              </div>
              <label className="px-4 py-3 bg-white rounded-2xl border border-slate-100 text-slate-700 text-sm font-bold flex items-center gap-2 self-start md:self-auto">
                {t("audience.period", "Period")}
                <select
                  value={days}
                  onChange={(e) => setDays(Number(e.target.value))}
                  className="bg-transparent outline-none"
                >
                  {AUDIENCE_DAY_OPTIONS.map((d) => (
                    <option key={d} value={d}>
                      {d === 1 ? t("audience.today", "Today") : t("audience.lastDays", "Last {{count}} days", { count: d })}
                    </option>
                  ))}
                </select>
              </label>
            </header>

            {isLoading && (
              <div className="flex justify-center py-16">
                <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 bg-red-50 text-red-700 rounded-2xl p-4" role="alert">
                <AlertCircle size={18} /> {error}
              </div>
            )}

            {summary && !isLoading && !error && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <Stat icon={Eye} label={t("audience.visits", "Visits")} value={summary.totalHits.toLocaleString()} />
                  <Stat
                    icon={Users}
                    label={t("audience.people", "Unique visitors")}
                    value={summary.uniqueVisitors.toLocaleString()}
                    hint={t("audience.peopleHint", "Different browsers or devices")}
                  />
                  <Stat
                    icon={Repeat}
                    label={t("audience.returning", "Returning")}
                    value={`${percent(summary.returningVisitorHits, known)}%`}
                    hint={t("audience.returningHint", "Had opened your page before")}
                  />
                  <Stat
                    icon={Clock}
                    label={t("audience.peak", "Busiest time")}
                    value={peak ? bucketLabel(peak.bucket) : "-"}
                    hint={peak ? t("audience.peakHint", "{{count}} visits", { count: peak.hits }) : undefined}
                  />
                </div>

                <section className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100">
                  <h2 className="text-lg font-bold text-slate-900 mb-3">
                    {summary.bucket === "hour" ? t("audience.perHour", "Visits per hour") : t("audience.perDay", "Visits per day")}
                  </h2>
                  {summary.totalHits === 0 ? (
                    <p className="text-sm text-slate-500">{empty}</p>
                  ) : (
                    <div
                      className="flex items-end gap-px h-40"
                      role="img"
                      aria-label={t("audience.chartLabel", "Chart of visits over time")}
                    >
                      {summary.trend.map((point) => (
                        <div
                          key={point.bucket}
                          className="flex-1 bg-blue-600 rounded-t-sm min-w-px"
                          style={{ height: `${(point.hits / trendMax) * 100}%`, minHeight: point.hits > 0 ? 2 : 0 }}
                          title={`${bucketLabel(point.bucket)}: ${point.hits}`}
                        />
                      ))}
                    </div>
                  )}
                  {summary.trend.length > 1 && (
                    <div className="flex justify-between text-xs text-slate-500 mt-2">
                      <span>{bucketLabel(summary.trend[0].bucket)}</span>
                      <span>{bucketLabel(summary.trend[summary.trend.length - 1].bucket)}</span>
                    </div>
                  )}
                </section>

                <div className="grid md:grid-cols-2 gap-4">
                  <BarList title={t("audience.cities", "Where they are")} items={topWithOther(summary.topCities, 8, unknown, other)} empty={empty} />
                  <BarList title={t("audience.devices", "Devices")} items={topWithOther(summary.topDeviceTypes, 5, unknown, other)} empty={empty} />
                  <BarList title={t("audience.systems", "Operating systems")} items={topWithOther(summary.topOperatingSystems, 6, unknown, other)} empty={empty} />
                  <BarList title={t("audience.browsers", "Browsers")} items={topWithOther(summary.topBrowsers, 6, unknown, other)} empty={empty} />
                  <BarList
                    title={t("audience.referrers", "How they found you")}
                    items={topWithOther(summary.topReferrals, 8, unknown, other)}
                    empty={t("audience.noReferrers", "Everyone arrived directly, for example from a QR code or a typed link.")}
                  />
                </div>

                <p className="text-xs text-slate-500">
                  {t(
                    "audience.note",
                    "Counts page loads of your web caption page. People using the ConferenceCaptioning app or App Clip are not included. A QR code or typed link shows no referrer."
                  )}
                  {bots > 0 && " " + t("audience.bots", "{{count}} visits came from bots and crawlers.", { count: bots })}
                </p>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Audience;
